import { z } from 'zod/v4'

import { defineScreen, type ScreenData } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

/*
 * «Mantenimiento» (`admin.maintenance`): the EFFECTIVE state of the maintenance mode (the scheduled window already
 * applied: inside it `enabled` is true, and an expired window comes back as nulls). The screen calls no mutation; the
 * service seeds the default configuration when none is stored yet, as the old route did. The mutations (enable,
 * disable, schedule, bypass IPs) stay on their own routes. Strict: a field that is not named here does not travel.
 */

export const adminMaintenanceStatusSchema = z.strictObject({
  enabled: z.boolean(),
  message: z.string(),
  /** The scheduled window, as ISO instants, or `null` when nothing is scheduled. */
  scheduledStart: z.iso.datetime().nullable(),
  scheduledEnd: z.iso.datetime().nullable(),
  /** The IPs that keep their access while the mode is on. */
  bypassIps: z.array(z.string()),
  /** The instant of the READ (the service stamps it when it builds the answer), not of the last change. */
  updatedAt: z.iso.datetime(),
})

export const adminMaintenanceScreen = defineScreen({
  id: 'admin.maintenance',
  path: '/api/screens/admin/maintenance',
  access: 'super_admin',
  query: z.strictObject({}),
  response: z.strictObject({
    status: screenSection(adminMaintenanceStatusSchema),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.maintenance'],
  budget: { requests: 1, statements: 4, bytes: 4_000, p95Ms: 150 },
  summary: 'Maintenance: whether the mode is on, its message, its schedule and the bypass IPs',
})

export type AdminMaintenanceStatus = z.infer<typeof adminMaintenanceStatusSchema>
export type AdminMaintenanceData = ScreenData<typeof adminMaintenanceScreen>
